import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Redirect,
  useHistory,
  useLocation,
} from 'react-router-dom'
import { GroupNavi, HeaderNavi } from '../../components/navigation'
import RfidEventRecorder from '../../components/rfid/RfidEventRecorder'
import RfidInventory from '../../components/rfid/RfidInventory'
import RfidTagManagement from '../../components/rfid/RfidTagManagement'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { getGroupRfidTags } from '../../modules/Rfid'
import { fetchGroupEventTypes } from '../../modules/QrTagEventTypes'
import { getTeamGroups } from '../../modules/Team'
import { getErrorString } from '../../utils/errorUtils'
import { OVERVIEW, RFID } from '../../constants/Paths'
import './Rfid.css'

type RfidMode = 'tags' | 'events' | 'inventory'

const RfidContent = () => {
  const history = useHistory()
  const location = useLocation()
  const groupIdParam = location.pathname.match(/^\/groups\/(\d+)\/rfid$/)?.[1] ?? ''
  const dispatch = useAppDispatch()
  const team = useAppSelector((state) => state.team)
  const groups = useMemo(() => team.groups ?? [], [team.groups])
  const [selectedGroupId, setSelectedGroupId] = useState<GroupId | null>(() => {
    const groupId = Number(groupIdParam)
    return Number.isFinite(groupId) ? groupId as GroupId : null
  })
  const [mode, setMode] = useState<RfidMode>('tags')
  const [tags, setTags] = useState<IRfidTag[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const tagsRequestIdRef = useRef(0)

  const selectedGroup = useMemo(() => (
    selectedGroupId
      ? groups.find((group) => group.id === selectedGroupId) ?? null
      : null
  ), [groups, selectedGroupId])
  const eventTypesEntry = useAppSelector((state) => (
    selectedGroupId ? state.qrTagEventTypes.entries[selectedGroupId] : undefined
  ))
  const eventTypes = useMemo(
    () => eventTypesEntry?.values ?? [],
    [eventTypesEntry],
  )

  useEffect(() => {
    if (!team.groups) dispatch(getTeamGroups(false))
  }, [dispatch, team.groups])

  useEffect(() => {
    if (groups.length === 0) {
      setSelectedGroupId(null)
      return
    }
    const routeGroupId = Number(groupIdParam)
    const routeGroup = groups.find((group) => group.id === routeGroupId)
    if (routeGroup) {
      setSelectedGroupId(routeGroup.id)
      return
    }

    const [fallbackGroup] = groups
    if (!fallbackGroup) return
    setSelectedGroupId(fallbackGroup.id)
    history.replace(RFID.replace(':group_id', String(fallbackGroup.id)))
  }, [groupIdParam, groups, history])

  const loadTags = useCallback(async () => {
    const requestId = tagsRequestIdRef.current + 1
    tagsRequestIdRef.current = requestId

    if (!selectedGroupId) {
      setTags([])
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const loadedTags = await getGroupRfidTags(selectedGroupId)
      if (tagsRequestIdRef.current !== requestId) return

      setTags(loadedTags)
    } catch (loadError) {
      if (tagsRequestIdRef.current !== requestId) return

      setTags([])
      setError(getErrorString(loadError))
    } finally {
      if (tagsRequestIdRef.current === requestId) setIsLoading(false)
    }
  }, [selectedGroupId])

  useEffect(() => {
    loadTags()
  }, [loadTags])

  useEffect(() => {
    if (selectedGroupId) dispatch(fetchGroupEventTypes({ groupId: selectedGroupId }))
  }, [dispatch, selectedGroupId])

  const reloadEventTypes = useCallback(() => {
    if (selectedGroupId) {
      dispatch(fetchGroupEventTypes({ groupId: selectedGroupId, force: true }))
    }
  }, [dispatch, selectedGroupId])

  const handleGroupChange = (value: string) => {
    const groupId = Number(value)
    if (!Number.isFinite(groupId)) return
    setSelectedGroupId(groupId as GroupId)
    setMode('tags')
    history.push(RFID.replace(':group_id', String(groupId)))
  }

  return (
    <div className="content" data-testid="rfid-page">
      <HeaderNavi />
      <GroupNavi>
        <main className="rfid-page">
          <div className="rfid-page__header">
            <div>
              <h2>RFID</h2>
              <p>RFIDタグの管理、イベント記録、保存しない棚卸しを行います。</p>
            </div>
            <div className="rfid-group-selector">
              <label htmlFor="rfid-group">グループ</label>
              <select
                id="rfid-group"
                value={selectedGroupId ? String(selectedGroupId) : ''}
                disabled={groups.length === 0}
                onChange={(event) => handleGroupChange(event.target.value)}
              >
                {groups.map((group) => (
                  <option key={group.id as number} value={group.id as number}>{group.name}</option>
                ))}
              </select>
            </div>
          </div>

          {groups.length === 0 && (
            <div className="rfid-message">利用できるグループがありません。</div>
          )}

          {selectedGroup && (
            <>
              <nav className="rfid-mode-tabs" aria-label="RFID機能">
                <button
                  type="button"
                  className={mode === 'tags' ? 'active' : ''}
                  aria-pressed={mode === 'tags'}
                  onClick={() => setMode('tags')}
                >
                  タグ管理
                </button>
                <button
                  type="button"
                  className={mode === 'events' ? 'active' : ''}
                  aria-pressed={mode === 'events'}
                  onClick={() => setMode('events')}
                >
                  イベント記録
                </button>
                <button
                  type="button"
                  className={mode === 'inventory' ? 'active' : ''}
                  aria-pressed={mode === 'inventory'}
                  onClick={() => setMode('inventory')}
                >
                  棚卸し
                </button>
              </nav>

              {isLoading && <div className="rfid-message">RFIDタグを読み込んでいます...</div>}
              {error && (
                <div className="rfid-message rfid-message--error">
                  {error}
                  {' '}
                  <button type="button" className="btn btn-link" onClick={loadTags}>
                    再読み込み
                  </button>
                </div>
              )}

              {!isLoading && !error && mode === 'tags' && (
                <RfidTagManagement
                  group={selectedGroup}
                  tags={tags}
                  onTagsChanged={loadTags}
                />
              )}
              {!isLoading && !error && mode === 'events' && (
                <RfidEventRecorder
                  group={selectedGroup}
                  tags={tags}
                  eventTypes={eventTypes}
                  eventTypesLoading={eventTypesEntry?.status === 'loading'}
                  eventTypesError={eventTypesEntry?.error ?? null}
                  onReloadEventTypes={reloadEventTypes}
                />
              )}
              {!isLoading && !error && mode === 'inventory' && (
                <RfidInventory key={selectedGroup.id as number} tags={tags} />
              )}
            </>
          )}
        </main>
      </GroupNavi>
    </div>
  )
}

const RfidPage = () => {
  const rfidTagEnabled = useAppSelector((state) => state.team.rfid_tag_enabled)

  return rfidTagEnabled === true
    ? <RfidContent />
    : <Redirect to={OVERVIEW} />
}

export default RfidPage
